import { useEffect, useState } from 'preact/hooks'
import {
  ankerHinweis, ankerZustand, anteilVerschluesseln, anteileErzeugen,
  SHARE_THRESHOLD, SHARE_TOTAL, verwahrerPublicKeyLaden,
} from '@fh/crypto'

import { api, ApiError, type Me } from '../api.js'

/**
 * Der soziale Anker (ADR-008).
 *
 * Die Oberfläche muss zwei Dinge leisten, die sich widersprechen: Sie soll den
 * Anker attraktiv machen — er ist das Netz gegen Totalverlust — und sie muss
 * den Preis nennen. Zwei Vertrauenspersonen, die zusammenarbeiten, kommen an
 * die eigenen Nachrichten. Wer das nicht will, soll es hier erfahren und nicht
 * später.
 */

interface Member { id: string; displayName: string }

interface AnkerStand {
  aktiveAnteile: number
  verwahrer: Array<{ personId: string; shareIndex: number }>
  verwahrtFuer: string[]
}

export function Anker({ me, geheimnis }: {
  me: Me
  /** Der Schlüssel, den der Anker schützt — kommt aus dem Schlüsselspeicher. */
  geheimnis: Uint8Array
}) {
  const [stand, setStand] = useState<AnkerStand | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [gewaehlt, setGewaehlt] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [fertig, setFertig] = useState(false)

  async function laden() {
    const [s, m] = await Promise.all([
      api.get<AnkerStand>('/recovery/anchor'),
      api.get<Member[]>('/family/members'),
    ])
    setStand(s)
    setMembers(m.filter((x) => x.id !== me.personId))
  }

  useEffect(() => { void laden() }, [])

  async function einrichten() {
    setFehler(null)
    setBusy(true)
    try {
      // Für jede Vertrauensperson den öffentlichen Schlüssel holen. Fehlt einer,
      // war die Person noch nie angemeldet — dann bringt es nichts, sie zu wählen.
      const schluessel = await Promise.all(gewaehlt.map(async (personId) => {
        const res = await api.get<{ key: { publicKey: string } | null }>(
          `/recovery/public-key/${personId}`,
        )
        if (!res.key) {
          const name = members.find((m) => m.id === personId)?.displayName ?? 'Diese Person'
          throw new Error(`${name} muss sich einmal anmelden, bevor sie Anteile verwahren kann.`)
        }
        return { personId, publicKey: await verwahrerPublicKeyLaden(res.key.publicKey) }
      }))

      const { anteile, pruefwert } = await anteileErzeugen(geheimnis)

      const eintraege = await Promise.all(schluessel.map(async (s, i) => ({
        holderId: s.personId,
        shareIndex: i + 1,
        shareEnc: await anteilVerschluesseln(anteile[i]!, s.publicKey),
      })))

      await api.put('/recovery/anchor', { anteile: eintraege, checkValue: pruefwert })
      setFertig(true)
      await laden()
    } catch (e) {
      setFehler(e instanceof ApiError ? e.message : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!stand) return <p class="fh-muted">Wird geladen…</p>

  const zustand = ankerZustand(stand.aktiveAnteile)

  return (
    <div class="fh-card">
      <h2 class="fh-h2">Notfall-Hilfe durch die Familie</h2>
      <p class="fh-muted">{ankerHinweis(zustand)}</p>

      {stand.verwahrtFuer.length > 0 && (
        <p class="fh-hinweis fh-hinweis--info">
          <span aria-hidden="true">🤝</span>
          <span>
            Du verwahrst {stand.verwahrtFuer.length === 1 ? 'einen Anteil' : 'Anteile'} für
            {' '}{stand.verwahrtFuer.length} {stand.verwahrtFuer.length === 1 ? 'Person' : 'Personen'}.
            Wenn jemand Hilfe braucht, siehst du das hier.
          </span>
        </p>
      )}

      {fertig && (
        <p class="fh-hinweis fh-hinweis--info" role="status">
          <span aria-hidden="true">✓</span>
          <span>Eingerichtet. Deine drei Vertrauenspersonen wurden nicht informiert —
          sie erfahren erst davon, wenn du sie wirklich brauchst.</span>
        </p>
      )}

      {zustand !== 'ok' && (
        <>
          <p>
            Wähle <strong>{SHARE_TOTAL} Personen</strong>, denen du vertraust.
            Wenn du alle deine Geräte verlierst, können <strong>zwei davon
            gemeinsam</strong> dir wieder Zugang verschaffen.
          </p>

          <fieldset class="fh-feld">
            <legend>Deine {SHARE_TOTAL} Vertrauenspersonen</legend>
            {members.map((m) => {
              const anGewaehlt = gewaehlt.includes(m.id)
              return (
                <label key={m.id} class="fh-feld fh-feld--inline">
                  <input
                    type="checkbox"
                    checked={anGewaehlt}
                    disabled={!anGewaehlt && gewaehlt.length >= SHARE_TOTAL}
                    onChange={() => setGewaehlt((alt) =>
                      anGewaehlt ? alt.filter((x) => x !== m.id) : [...alt, m.id])}
                  />
                  <span>{m.displayName}</span>
                </label>
              )
            })}
          </fieldset>

          {members.length < SHARE_TOTAL && (
            <p class="fh-muted">
              Dafür braucht ihr mindestens {SHARE_TOTAL + 1} Familienmitglieder.
              Lade zuerst weitere ein.
            </p>
          )}

          {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

          <button
            class="fh-btn"
            disabled={busy || gewaehlt.length !== SHARE_TOTAL}
            onClick={() => void einrichten()}
          >
            {busy ? 'Einen Moment…' : 'Einrichten'}
          </button>
        </>
      )}

      {/*
        Der Preis, offen genannt. Diese Zeilen wegzulassen wäre bequemer und
        unehrlich: Der Anker verschiebt das Bedrohungsmodell (T13), und wer ihn
        einrichtet, sollte wissen wohin.
      */}
      <details class="fh-card">
        <summary>Was das bedeutet</summary>
        <p>
          <strong>{SHARE_THRESHOLD} deiner {SHARE_TOTAL} Vertrauenspersonen können
          gemeinsam</strong> an deine Nachrichten — auch ohne dich. Einzeln kann
          es niemand.
        </p>
        <p>
          Damit das nicht heimlich passiert, dauert jede Wiederherstellung
          <strong> drei Tage</strong>. In dieser Zeit bekommst du auf allen deinen
          Geräten täglich eine Meldung und kannst sie mit einem Tippen abbrechen.
        </p>
        <p class="fh-muted">
          Wenn dir das zu weit geht: Richte den Anker nicht ein. Dein
          Notfall-Blatt reicht dann als einziger Rückweg — bewahre es gut auf.
        </p>
      </details>
    </div>
  )
}

/**
 * Warnbanner, wenn jemand eine Wiederherstellung für dieses Konto angestoßen hat.
 *
 * Steht bewusst weit oben und ist nicht wegklickbar: Genau das ist der Schutz
 * aus ADR-008. Ein Countdown, den niemand sieht, hält niemanden auf.
 */
export function WiederherstellungsWarnung() {
  const [anfragen, setAnfragen] = useState<Array<{
    id: string; verifyCode: string; releaseAt: string | null; zustimmungen: number
  }>>([])

  async function laden() {
    const res = await api.get<{ eigene: typeof anfragen }>('/recovery/requests')
    setAnfragen(res.eigene)
  }

  useEffect(() => {
    void laden()
    const t = setInterval(() => void laden(), 60_000)
    return () => clearInterval(t)
  }, [])

  if (anfragen.length === 0) return null
  const a = anfragen[0]!

  return (
    <div class="fh-banner fh-banner--warnung" role="alert">
      <span aria-hidden="true">🚨</span>
      <div>
        <strong>Jemand versucht, Zugang zu deinen Nachrichten zu bekommen.</strong>
        <p class="fh-muted">
          {a.zustimmungen} von {SHARE_THRESHOLD} Vertrauenspersonen haben zugestimmt.
          {a.releaseAt && ` Freigabe am ${new Date(a.releaseAt).toLocaleDateString('de-DE')}.`}
          {' '}Warst du das nicht, brich es jetzt ab.
        </p>
      </div>
      <button
        class="fh-btn fh-btn--gefahr fh-banner__action"
        onClick={() => { void api.post(`/recovery/requests/${a.id}/veto`).then(() => laden()) }}
      >
        Das war ich nicht — abbrechen
      </button>
    </div>
  )
}
